import { Component, Element, h, Host, Prop } from '@stencil/core'
import 'iconify-icon'

/**
 * O componente `br-icon` fornece uma maneira flexível e dinâmica de incorporar ícones nas aplicações.
 * Utilizando a biblioteca Iconify, ele permite o uso de uma ampla variedade de ícones.
 *
 * Com opções para especificar a altura e largura, adicionar classes CSS personalizadas, e definir como o ícone
 * deve ser rotacionado ou espelhado, o `br-icon` oferece aos desenvolvedores as ferramentas necessárias para
 * integrar ícones de forma consistente com o estilo e design de suas aplicações, melhorando a experiência do usuário
 * e a clareza das interfaces.
 *
 * Para mais informações sobre quais ícones estão disponíveis, consulte a documentação do [Iconify](https://iconify.design/).
 */
@Component({
  tag: 'br-icon',
  styleUrl: 'icon.scss',
  shadow: true,
})
export class Icon {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrIconElement

  /**
   * Nome do ícone a ser exibido, utilizando a biblioteca Iconify.
   * Este nome deve corresponder ao nome do ícone definido na biblioteca para que ele seja exibido corretamente.
   */
  @Prop({ reflect: true }) readonly iconName: string

  /**
   * Define a altura do ícone. Pode ser especificada em qualquer unidade CSS válida, como pixels (px), ems (em), rems (rem), etc.
   * O valor padrão é '16'.
   */
  @Prop({ reflect: true }) readonly height: string = '16'

  /**
   * Identificador único.
   * Caso não seja fornecido, um ID gerado automaticamente será usado.
   */
  @Prop({ reflect: true }) readonly customId: string = `br-icon-${iconId++}`

  /**
   * Define a largura do ícone. Pode ser especificada em qualquer unidade CSS válida, como pixels (px), ems (em), rems (rem), etc.
   * O valor padrão é '24'.
   */
  @Prop({ reflect: true }) readonly width: string = '24'

  /**
   * Permite adicionar classes CSS adicionais ao ícone.
   * Use esta propriedade para aplicar estilos personalizados ao ícone, além dos estilos padrão.
   */
  @Prop({ reflect: true }) readonly cssClasses?: string

  /**
   * Se definido como verdadeiro, o ícone será alinhado verticalmente ao texto ao seu redor.
   * Útil quando o ícone é usado em linha com texto para garantir que esteja alinhado corretamente com o texto.
   * O valor padrão é `false`.
   */
  @Prop({ reflect: true }) readonly isInline?: boolean = false

  /**
   * Define o ângulo de rotação do ícone.
   */
  @Prop({ reflect: true }) readonly rotate?: '90deg' | '180deg' | '270deg'

  /**
   * Define o tipo de espelhamento do ícone.
   */
  @Prop({ reflect: true }) readonly flip?: 'horizontal' | 'vertical'

  /**
   * Determina se o ícone pode receber foco.
   * Se definido como verdadeiro, o ícone pode ser navegado usando Tab.
   * O valor padrão é `false`.
   */
  @Prop({ reflect: true }) readonly isFocusable?: boolean = false

  /**
   * Controla o comportamento de carregamento do ícone através do observer interno do Iconify.
   *
   * Comportamento:
   * - Padrão (propriedade não definida): carregamento imediato aplicando `noobserver` para evitar problemas de layout shift
   * - `lazy={true}`: ativa o observer para carregamento lazy otimizado (útil em documentos longos com muitos ícones)
   * - `lazy={false}`: carregamento imediato aplicando `noobserver` (mesmo comportamento do padrão)
   *
   * **Nota**: O padrão foi alterado para carregamento imediato para resolver problemas de deslocamento de layout
   * que ocorriam quando ícones eram carregados depois do conteúdo inicial.
   */
  @Prop({ reflect: true }) readonly lazy?: boolean

  /**
   * Retorna o valor efetivo para o comportamento "lazy" do ícone.
   *
   * Explicação:
   * - Se o atributo 'lazy' estiver presente no elemento, o método retorna o valor da propriedade this.lazy (true ou false).
   * - Se o atributo 'lazy' NÃO estiver presente, assume-se o comportamento padrão, retornando false (ou seja, carregamento imediato ativado).
   *
   * Isso permite distinguir entre o caso em que o usuário explicitamente define lazy={true} (carregamento lazy)
   * e o caso em que o atributo não é informado (carregamento imediato por padrão).
   */
  private get effectiveLazy(): boolean {
    return this.el.hasAttribute('lazy') ? this.lazy === true : false
  }

  componentDidLoad() {
    const iconElement = this.el.shadowRoot.querySelector('iconify-icon')
    if (iconElement !== null) {
      if (this.isFocusable && !this.el.hasAttribute('aria-label')) {
        const formattedIconName = this.iconName
          .split(':')
          .pop()
          .replace(/-/g, ' ')
          .replace(/^[a-z]/, (match) => match.toUpperCase())

        this.el.setAttribute('aria-label', `Ícone: ${formattedIconName}`)
      }
    }
  }

  render() {
    return (
      <Host>
        <iconify-icon
          id={this.customId}
          flip={this.flip}
          width={this.width}
          class={this.cssClasses}
          icon={this.iconName}
          inline={this.isInline}
          height={this.height}
          rotate={this.rotate}
          role={this.isFocusable ? 'img' : null}
          tabindex={this.isFocusable ? 0 : '-1'}
          aria-hidden={this.isFocusable ? null : 'true'}
          focusable={this.isFocusable ? true : 'false'}
          {...(this.effectiveLazy === false ? { noobserver: true } : {})}
        ></iconify-icon>
      </Host>
    )
  }
}

let iconId = 0
